import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { A, pipe } from "@mobily/ts-belt"
import type { Comment } from "@shared/models/comment"
import FindDoc from "cc-frontend/helpers/find-doc"
import FindManyAltogether from "cc-frontend/helpers/find-many-altogether"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type Session from "cc-frontend/services/session"
import { use } from "ember-could-get-used-to-this"
import styles from "./lesson-comment.module.css"

interface Sig {
  Args: {
    comment: Comment
  }
}

export default class CommentComponent extends Component<Sig> {
  readonly styles = styles

  @service lessonEditor!: LessonEditor
  @service session!: Session

  @use commenter = new FindDoc(() => ["user-summary", this.args.comment.attributes.commenterId])
  @use otherRecipients = new FindManyAltogether(() => ["user-summary", this.otherRecipientIds])

  get commenterName() {
    if (this.commenter.content === null) return ""
    return (
      this.commenter.content.attributes.firstName + " " + this.commenter.content.attributes.lastName
    )
  }

  get isPrivate() {
    const recipients = this.args.comment.attributes.recipients
    return !recipients.any((recipient) => recipient.type === "planbook")
  }

  get isRecipient() {
    return pipe(
      this.args.comment.attributes.recipients,
      A.filter((recipient) => recipient.type === "user"),
      A.map((recipient) => recipient.id),
      A.includes(this.session.userId!)
    )
  }

  get otherRecipientIds() {
    return pipe(
      this.args.comment.attributes.recipients,
      A.filter((recipient) => recipient.type === "user"),
      A.map((recipient) => recipient.id),
      A.removeFirst(this.session.userId!)
    )
  }

  get otherRecipientNamesString() {
    return (
      this.otherRecipients.content
        ?.map((user) => `${user.attributes.firstName} ${user.attributes.lastName}`)
        .join(", ") || ""
    )
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::CardStacks::Lesson::LessonComment": typeof CommentComponent
  }
}
